import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Plus, Search } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import RoleBadge from "@/components/RoleBadge";
import StaffAvatar from "@/components/StaffAvatar";
import { useAuth } from "@/context/AuthContext";
import { Megaphone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EMPTY_FORM = {
  first_name: "", surname: "", middle_name: "", preferred_name: "",
  email: "", role: "travel_trainer", job_title: "", phone: "", start_date: "", password: "",
};

const COMPLIANCE_STYLES = {
  valid: "bg-ukgreen-light text-ukgreen border-transparent",
  expiring_soon: "bg-amber-100 text-amber-800 border-transparent",
  expired: "bg-red-50 text-red-700 border-transparent",
  missing: "bg-slate-100 text-slate-600 border-transparent",
  not_applicable: "bg-slate-100 text-slate-500 border-transparent",
};

const COMPLIANCE_LABELS = {
  valid: "Valid", expiring_soon: "Expiring soon", expired: "Expired", missing: "Missing", not_applicable: "N/A",
};

const EMPLOYMENT_LABELS = {
  active: "Active", inactive: "Inactive", suspended: "Suspended", left_organisation: "Left organisation",
};

export function ComplianceBadge({ status, testid }) {
  return (
    <Badge data-testid={testid} className={COMPLIANCE_STYLES[status] || COMPLIANCE_STYLES.missing}>
      {COMPLIANCE_LABELS[status] || status}
    </Badge>
  );
}

export default function StaffList() {
  const { user, hasPermission } = useAuth();
  const canCreateStaff = hasPermission("staff:create");
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [employmentFilter, setEmploymentFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [complianceFilter, setComplianceFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (roleFilter !== "all") params.role = roleFilter;
      if (employmentFilter !== "all") params.employment_status = employmentFilter;
      if (accountFilter !== "all") params.status = accountFilter;
      if (complianceFilter !== "all") params.compliance = complianceFilter;
      const { data } = await api.get("/staff", { params });
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, roleFilter, employmentFilter, accountFilter, complianceFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const onCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name, surname: form.surname, email: form.email, role: form.role,
      };
      ["middle_name", "preferred_name", "job_title", "phone", "start_date", "password"].forEach((k) => {
        if (form[k]) payload[k] = form[k];
      });
      const { data } = await api.post("/staff", payload);
      toast.success(data.set_password_email_sent ? "Account created — set-password email sent" : "Account created");
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const canCreateManager = user?.role === "super_admin";

  const fmtLogin = (v) => {
    if (!v) return "Never";
    try {
      return format(new Date(v), "dd MMM yyyy");
    } catch {
      return "—";
    }
  };

  return (
    <div data-testid="staff-list-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Staff</h1>
          <p className="text-sm text-slate-600 mt-1">Individual accounts only — shared logins are not permitted.</p>
        </div>
        {["admin_manager", "super_admin"].includes(user?.role) && <BroadcastManager />}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {canCreateStaff && (
          <DialogTrigger asChild>
            <Button data-testid="staff-add-button" className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Add staff member
            </Button>
          </DialogTrigger>
          )}
          <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Add staff member</DialogTitle>
              <DialogDescription>Creates an individual staff account with its own login.</DialogDescription>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4" data-testid="staff-create-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>First name *</Label>
                  <Input data-testid="staff-create-firstname-input" required value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Surname *</Label>
                  <Input data-testid="staff-create-surname-input" required value={form.surname}
                    onChange={(e) => setForm({ ...form, surname: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Middle name</Label>
                  <Input data-testid="staff-create-middlename-input" value={form.middle_name}
                    onChange={(e) => setForm({ ...form, middle_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Preferred name</Label>
                  <Input data-testid="staff-create-preferredname-input" value={form.preferred_name}
                    onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Email address *</Label>
                <Input data-testid="staff-create-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Role *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="staff-create-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="travel_trainer" data-testid="role-option-travel-trainer">Travel Trainer</SelectItem>
                      {canCreateManager && <SelectItem value="admin_manager" data-testid="role-option-admin-manager">Admin / Manager</SelectItem>}
                      {canCreateManager && <SelectItem value="super_admin" data-testid="role-option-super-admin">Super Admin</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Job title</Label>
                  <Input data-testid="staff-create-jobtitle-input" value={form.job_title}
                    onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Telephone</Label>
                  <Input data-testid="staff-create-phone-input" value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Start date</Label>
                  <Input data-testid="staff-create-startdate-input" type="date" value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Initial password (optional)</Label>
                <Input data-testid="staff-create-password-input" type="password" autoComplete="new-password"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <p className="text-xs text-slate-500">
                  Leave blank to email the staff member a secure set-password link. Passwords are never logged.
                </p>
              </div>
              <DialogFooter>
                <Button data-testid="staff-create-submit-button" type="submit" disabled={saving}
                  className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="relative lg:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="staff-search-input" placeholder="Search name, staff ID or email…"
            value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="staff-role-filter" className="h-11"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="filter-role-all">All roles</SelectItem>
            <SelectItem value="super_admin">Manager</SelectItem>
            <SelectItem value="admin_manager">Co-ordinator</SelectItem>
            <SelectItem value="travel_trainer">Travel Trainer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={employmentFilter} onValueChange={setEmploymentFilter}>
          <SelectTrigger data-testid="staff-employment-filter" className="h-11"><SelectValue placeholder="Employment" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="filter-employment-all">All employment</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="left_organisation">Left organisation</SelectItem>
          </SelectContent>
        </Select>
        <Select value={complianceFilter} onValueChange={setComplianceFilter}>
          <SelectTrigger data-testid="staff-compliance-filter" className="h-11"><SelectValue placeholder="Compliance" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="filter-compliance-all">All compliance</SelectItem>
            <SelectItem value="valid">Valid</SelectItem>
            <SelectItem value="expiring_soon">Expiring soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="missing">Missing</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-600" data-testid="staff-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading staff…
          </div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-600" data-testid="staff-empty">No staff members found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Staff ID</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden lg:table-cell">Contact</TableHead>
                <TableHead className="hidden md:table-cell">Employment</TableHead>
                <TableHead>Compliance</TableHead>
                <TableHead className="hidden md:table-cell">Account</TableHead>
                <TableHead className="hidden lg:table-cell">Cases</TableHead>
                <TableHead className="hidden lg:table-cell">Last login</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} data-testid={`staff-row-${s.email}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <StaffAvatar photoId={s.profile?.photo_document_id} name={s.name} testid={`staff-avatar-${s.staff_number}`} />
                      <div>
                        <Link to={`/staff/${s.id}`} data-testid={`staff-view-link-${s.email}`}
                          className="font-medium text-primary hover:underline">
                          {s.name}
                        </Link>
                        <p className="text-xs text-slate-500">{s.profile?.job_title || "—"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{s.staff_number || "—"}</TableCell>
                  <TableCell><RoleBadge role={s.role} /></TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600">
                    <p>{s.email}</p>
                    <p className="text-xs text-slate-500">{s.profile?.phone || ""}</p>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="secondary" className="text-xs">{EMPLOYMENT_LABELS[s.profile?.employment_status || "active"]}</Badge>
                  </TableCell>
                  <TableCell><ComplianceBadge status={s.compliance_status} testid={`staff-compliance-${s.staff_number}`} /></TableCell>
                  <TableCell className="hidden md:table-cell">
                    {s.status === "active" ? (
                      <Badge className="bg-ukgreen-light text-ukgreen border-transparent">Active</Badge>
                    ) : (
                      <Badge className="bg-red-50 text-red-700 border-transparent">Deactivated</Badge>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600" data-testid={`staff-cases-${s.staff_number}`}>
                    {s.assigned_cases ?? 0}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-slate-600">{fmtLogin(s.last_login_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function BroadcastManager() {
  const [open, setOpen] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [all, setAll] = useState(true);
  const [targets, setTargets] = useState([]);
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/staff", { params: { status: "active" } })
      .then((r) => setTrainers(r.data.items || [])).catch(() => {});
    api.get("/broadcasts").then((r) => setMessages(r.data.items || [])).catch(() => {});
  }, [open]);

  const toggleTarget = (id) => setTargets((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/broadcasts", {
        title: title.trim(), body: body.trim(),
        all_staff: all, recipient_ids: all ? [] : targets,
        expires_hours: expiry ? parseInt(expiry, 10) : null,
      });
      toast.success("Urgent message sent — recipients must acknowledge it before they can continue");
      setTitle(""); setBody(""); setTargets([]); setAll(true); setExpiry("");
      const { data } = await api.get("/broadcasts");
      setMessages(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const closeMsg = async (id) => {
    try {
      await api.post(`/broadcasts/${id}/close`);
      setMessages((cur) => cur.map((m) => (m.id === id ? { ...m, active: false } : m)));
      toast.success("Message closed — it no longer blocks staff");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <Button variant="outline" className="h-11 border-red-300 text-red-700 hover:bg-red-50 font-semibold"
        data-testid="broadcast-open-button" onClick={() => setOpen(true)}>
        <Megaphone className="h-4 w-4 mr-2" /> Urgent message
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl bg-white max-h-[90vh] overflow-y-auto" data-testid="broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Urgent message</DialogTitle>
            <DialogDescription>
              Every recipient — trainer, co-ordinator or manager — must read and acknowledge this message on a blocking screen before they can continue using the app.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Route change — tomorrow morning" maxLength={150} />
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea data-testid="broadcast-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} />
            </div>
            <div className="space-y-1.5">
              <Label>Send to</Label>
              <div className="flex flex-wrap gap-2">
                <button type="button" data-testid="broadcast-target-all" onClick={() => setAll(true)}
                  className={`h-9 px-3 rounded-md border text-xs font-semibold transition-colors ${all ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  All staff
                </button>
                <button type="button" data-testid="broadcast-target-pick" onClick={() => setAll(false)}
                  className={`h-9 px-3 rounded-md border text-xs font-semibold transition-colors ${!all ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                  Choose staff
                </button>
              </div>
              {!all && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="broadcast-trainer-picker">
                  {trainers.map((t) => (
                    <button key={t.id} type="button" data-testid={`broadcast-trainer-${t.id}`} onClick={() => toggleTarget(t.id)}
                      className={`h-9 px-3 rounded-md border text-xs font-medium transition-colors ${targets.includes(t.id) ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                      {t.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Auto-close after (optional)</Label>
              <div className="flex flex-wrap gap-2" data-testid="broadcast-expiry-picker">
                {[["", "Never"], ["4", "4 hours"], ["24", "24 hours"], ["48", "48 hours"], ["168", "1 week"]].map(([v, label]) => (
                  <button key={v} type="button" data-testid={`broadcast-expiry-${v || "never"}`} onClick={() => setExpiry(v)}
                    className={`h-9 px-3 rounded-md border text-xs font-medium transition-colors ${expiry === v ? "bg-primary text-white border-primary" : "bg-white text-slate-700 border-border hover:bg-muted"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">After this time the message stops blocking staff automatically (e.g. if someone is off sick).</p>
            </div>
            <Button data-testid="broadcast-send-button" disabled={busy || title.trim().length < 3 || body.trim().length < 3 || (!all && targets.length === 0)}
              onClick={send} className="h-11 w-full bg-red-600 hover:bg-red-700 text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send — requires acknowledgement"}
            </Button>

            {messages.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-border" data-testid="broadcast-list">
                <Label>Recent messages</Label>
                {messages.slice(0, 8).map((m) => (
                  <div key={m.id} className="rounded-md border border-border p-3 text-sm" data-testid={`broadcast-row-${m.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-slate-900">{m.title}</p>
                      {m.active
                        ? <span className="text-[11px] font-semibold text-red-700">ACTIVE</span>
                        : <span className="text-[11px] text-slate-400">closed</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {m.created_by_name} · {new Date(m.created_at).toLocaleString("en-GB")} · acknowledged {m.ack_count}/{m.target_count}
                      {m.expires_at ? ` · auto-closes ${new Date(m.expires_at).toLocaleString("en-GB")}` : ""}
                    </p>
                    <div className="mt-1.5 space-y-0.5">
                      {(m.targets_status || []).map((t) => (
                        <p key={t.user_id} className="text-xs" data-testid={`broadcast-ack-status-${m.id}-${t.user_id}`}>
                          {t.acknowledged_at
                            ? <span className="text-emerald-700">✓ {t.name} — {new Date(t.acknowledged_at).toLocaleString("en-GB")}</span>
                            : <span className="text-amber-700">… {t.name} — not yet acknowledged</span>}
                        </p>
                      ))}
                    </div>
                    {m.active && (
                      <Button variant="outline" className="h-8 mt-2 text-xs" data-testid={`broadcast-close-${m.id}`} onClick={() => closeMsg(m.id)}>
                        Close message
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
